'use client';

import { motion } from 'framer-motion';
import { ReactNode } from 'react';

interface AnimatedButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'gradient' | 'success' | 'warning' | 'info' | 'edit' | 'delete' | 'cancel' | 'save' | 'activate' | 'deactivate';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  icon?: ReactNode;
}

const variants = {
  // Standard variants
  primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-200',
  secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200',
  danger: 'bg-red-500 hover:bg-red-600 text-white shadow-sm shadow-red-200',
  ghost: 'bg-transparent hover:bg-gray-100 text-gray-600',
  gradient: 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white shadow-lg shadow-indigo-500/25',
  
  // Action-based variants
  success: 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm shadow-emerald-200',
  warning: 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm shadow-amber-200',
  info: 'bg-sky-500 hover:bg-sky-600 text-white shadow-sm shadow-sky-200',
  
  // Specific action variants
  edit: 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm shadow-blue-200',
  delete: 'bg-red-500 hover:bg-red-600 text-white shadow-sm shadow-red-200',
  cancel: 'bg-gray-400 hover:bg-gray-500 text-white shadow-sm shadow-gray-200',
  save: 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm shadow-emerald-200',
  activate: 'bg-green-500 hover:bg-green-600 text-white shadow-sm shadow-green-200',
  deactivate: 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm shadow-orange-200',
};

const sizes = {
  xs: 'px-2 py-1 text-xs gap-1',
  sm: 'px-2.5 py-1.5 text-xs gap-1.5',
  md: 'px-3.5 py-2 text-sm gap-2',
  lg: 'px-5 py-2.5 text-base gap-2',
};

export function AnimatedButton({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
  icon,
}: AnimatedButtonProps) {
  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled}
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      transition={{ duration: 0.15 }}
      className={`
        inline-flex items-center justify-center font-medium rounded-lg
        transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]} ${sizes[size]} ${className}
      `}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </motion.button>
  );
}
